import { useEffect, useRef } from 'react';
import { CSVLink } from 'react-csv';
import { useDispatch } from 'react-redux';
import {
  clearReceiptsReport,
  FETCH_RECEIPTS_REPORT,
  fetchReceiptsCVS,
  updateReceiptDisplayMode,
} from '~/actions';
import { Button, Column, Label, Pressable, Row, Text } from '~/commonComponents';
import { AnimatedBoxShowHide, Box } from '~/commonComponents/Box';
import InputSelect from '~/commonComponents/InputSelect';
import Tooltip from '~/components/Tooltip';
import { paymentHistoryFilterOptionsLabels } from '~/constants';
import {
  getShouldHideRequestReportButton,
  getShouldUseReportV2,
  shouldShowDownloadReceiptButton,
} from '~/growthbook';
import { useLoading } from '~/hooks';
import { useAppSelector } from '~/hooks/useAppSelector';
import ReceiptsAllIcon from '~/images/receipts-all-icon.svg';
import ReceiptsShiftIcon from '~/images/receipts-shift-icon.svg';
import { Colors } from '~/themes/colors';
import { getFilterDateRage } from '../../utils';
import { DownloadReportButton } from './DownloadReportButton';

interface IProps {
  displayMode: string;
  filter: string;
  onFilterChange: (val: string) => void;
  onShowRequestReportPopup: () => void;
}

export const Toolbar = ({
  displayMode,
  filter,
  onFilterChange,
  onShowRequestReportPopup,
}: IProps) => {
  const dispatch = useDispatch();

  const receiptReport = useAppSelector((state) => state.job.receiptReport);
  const { isLoading: isCSVReportLoading } = useLoading([FETCH_RECEIPTS_REPORT]);
  const isHideRequestReportButton = useRef(getShouldHideRequestReportButton()).current;

  const cvsDownload = useRef<any>(null);

  useEffect(() => {
    if (cvsDownload.current?.link && receiptReport !== '') {
      cvsDownload.current.link.click();
      dispatch(clearReceiptsReport());
    }
  }, [dispatch, receiptReport]);

  const onDownloadCSVPressed = () => {
    dispatch(fetchReceiptsCVS({ ...getFilterDateRage(filter) }));
  };

  const isReportV2Enabled = useRef(getShouldUseReportV2()).current;

  const isShowDownloadReportV1 = !isReportV2Enabled && shouldShowDownloadReceiptButton();

  return (
    <Row width="100%" marginBottom={24} marginTop={8} alignItems="flex-end">
      <AnimatedBoxShowHide isShow={displayMode === 'all'}>
        <Column height={100}>
          <Row gap={4}>
            <Label text="Transaction Date:" required={false} marginBottom={0} />
            {/* @ts-expect-error -- Tooltip still written in JavaScript */}
            <Tooltip content="If you are interested in transaction activity before August 2024, please request a report on this page and indicate the time range of interest." />
          </Row>
          <Box width={212}>
            {/* @ts-expect-error -- InputSelect still written in JavaScript */}
            <InputSelect
              value={filter}
              setValue={onFilterChange}
              isSelectVisible={false}
              options={
                Object.entries(paymentHistoryFilterOptionsLabels).map(([key, name]) => ({
                  value: key,
                  title: name,
                })) as any
              }
            />
          </Box>
        </Column>
      </AnimatedBoxShowHide>
      <Row marginLeft="auto" alignItems="center" gap={20}>
        {!isHideRequestReportButton && (
          <AnimatedBoxShowHide isShow={displayMode === 'all'} style={{ width: 175 }}>
            <Box height={32} display="block" />
            <Button
              text="Request Report"
              textStyle={{
                fontSize: 14,
                fontWeight: 700,
              }}
              style={{
                alignItems: 'center',
                backgroundColor: Colors.secondary_500,
                borderRadius: 25,
                cursor: 'pointer',
                display: 'flex',
                height: 30,
                justifyContent: 'center',
                marginTop: 0,
                padding: '8px 12px',
                width: 'auto',
              }}
              onClick={onShowRequestReportPopup}
            />
            <Box height={32} display="block" />
          </AnimatedBoxShowHide>
        )}

        {isShowDownloadReportV1 ? (
          <AnimatedBoxShowHide isShow={displayMode === 'all'}>
            <CSVLink
              ref={cvsDownload}
              className="btn btn-success"
              data={receiptReport}
              enclosingCharacter=""
              filename="receiptReport.csv"
            />
            <Button
              isLoading={isCSVReportLoading}
              text="Download Report"
              textStyle={{
                fontFamily: 'Nunito',
                fontWeight: 'bold',
                fontSize: 16,
              }}
              style={{
                alignItems: 'center',
                borderRadius: 25,
                cursor: displayMode === 'all' ? 'pointer' : 'default',
                display: 'flex',
                height: 30,
                justifyContent: 'center',
                marginTop: 0,
                padding: '8px 16px',
                width: 'auto',
              }}
              onClick={onDownloadCSVPressed}
              disabled={displayMode === 'shift'}
            />
          </AnimatedBoxShowHide>
        ) : (
          <AnimatedBoxShowHide isShow={displayMode === 'all'}>
            <DownloadReportButton />
          </AnimatedBoxShowHide>
        )}

        <Column alignItems="center" height={100}>
          <Box height={32} display="block" />
          <Row>
            <Pressable onPress={() => dispatch(updateReceiptDisplayMode('shift'))}>
              <Box
                className={`button_group_container_first_option ${
                  displayMode === 'shift' ? 'selected_button_group_option' : ''
                }`}
              >
                <img src={ReceiptsShiftIcon} alt="receipts_shift_icon" />
              </Box>
            </Pressable>
            <div
              onClick={() => dispatch(updateReceiptDisplayMode('all'))}
              className={`button_group_container_last_option ${
                displayMode === 'all' ? 'selected_button_group_option' : ''
              }`}
            >
              <img src={ReceiptsAllIcon} alt="receipts_all_icon" />
            </div>
          </Row>
          <Row marginTop={5} columnGap={20}>
            <Text margin={0}>Shift</Text>
            <Text margin={0}>All</Text>
          </Row>
        </Column>
      </Row>
    </Row>
  );
};
